import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useKbArticle } from '@/queries/kb'
import { api } from '@/lib/api'
import { kbImageApiPath } from './kbImagePath'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { formatDate } from '@/lib/dates'
import { KbRestrictedBadge } from './KbRestrictedBadge'

function KbInlineImage({ src, alt }: { src?: string; alt?: string }) {
  const [resolved, setResolved] = useState<string | null>(null)
  const apiPath = kbImageApiPath(src)

  if (!apiPath) {
    return <img src={src} alt={alt ?? ''} className="rounded border border-border max-h-[480px]" />
  }
  if (!resolved) {
    // Through the authed client: the API only serves an image the reader has an article for.
    api<{ url?: string }>(apiPath, { query: { json: 1 } })
      .then(j => { if (j?.url) setResolved(j.url) })
      .catch(() => undefined)
    return <span className="text-xs text-muted-foreground">{alt ?? 'image'} (loading…)</span>
  }
  return <img src={resolved} alt={alt ?? ''} className="rounded border border-border max-h-[480px]" />
}

export function KbArticleView({ slug }: { slug: string }) {
  const { data, isLoading, error } = useKbArticle(slug)
  const { user } = useAuth0()
  const isAdmin = Boolean((user as Record<string, unknown> | undefined)?.['https://app.etainabl.com/isAdmin'])

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-3xl p-6">
        <p className="text-sm text-muted-foreground mb-4">Article not found.</p>
        <Link to="/kb"><Button variant="outline" size="sm"><ArrowLeft className="mr-1.5 h-4 w-4" />Back to Knowledgebase</Button></Link>
      </div>
    )
  }

  return (
    <article className="container mx-auto max-w-3xl p-6">
      <nav aria-label="Breadcrumb" className="mb-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          <li>
            <Link to="/kb" className="inline-flex items-center gap-1.5 hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" />
              Knowledgebase
            </Link>
          </li>
          {data.collection && (
            <>
              <li aria-hidden><ChevronRight className="h-3.5 w-3.5" /></li>
              <li>
                <Link to={`/kb/c/${data.collection.slug}` as never} className="hover:text-foreground">
                  {data.collection.name}
                </Link>
              </li>
            </>
          )}
        </ol>
      </nav>

      {!data.isPublished && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 mb-4">
          This article is a draft. Only admins can see it.
        </div>
      )}

      <header className="mb-6 space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">{data.title}</h1>
        {data.subtitle && <p className="text-lg text-muted-foreground">{data.subtitle}</p>}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          Updated {formatDate(data.updatedAt)}
          {isAdmin && <KbRestrictedBadge article={data} detailed />}
        </div>
      </header>

      <div className="prose prose-sm sm:prose max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ img: KbInlineImage as never }}>{data.content || '*No content yet.*'}</ReactMarkdown>
      </div>

      {isAdmin && (
        <div className="mt-8 pt-6 border-t">
          <Link to={`/admin/kb/${data._id}` as never}>
            <Button size="sm" variant="outline">Edit article</Button>
          </Link>
        </div>
      )}
    </article>
  )
}
